import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { telegramService } from '../../services/telegram.service'
import api from '../../services/api'

interface TrendAnalysis {
  id: number
  channel_id: number
  channel_title: string
  period_start: string
  period_end: string
  top_topics: string[]
  trending_keywords: string[]
  engagement_insights: string
  created_at: string
}

export default function TrendsPage() {
  const [selectedChannel, setSelectedChannel] = useState<number | null>(null)

  // Check Telegram status
  const { data: telegramStatus } = useQuery({
    queryKey: ['telegramStatus'],
    queryFn: () => telegramService.getStatus(),
  })

  // Fetch channels
  const { data: channels = [] } = useQuery({
    queryKey: ['channels'],
    queryFn: async () => {
      const response = await api.get('/channels')
      return response.data
    },
    enabled: telegramStatus?.connected,
  })

  // Fetch trend analyses
  const { data: trends = [], isLoading } = useQuery({
    queryKey: ['trends', selectedChannel],
    queryFn: async () => {
      const params = selectedChannel ? { channel_id: selectedChannel } : {}
      const response = await api.get<TrendAnalysis[]>('/trends', { params })
      return response.data
    },
    enabled: telegramStatus?.connected,
  })

  if (!telegramStatus?.connected) {
    return (
      <div className="pt-14">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Тренды</h1>
          <p className="text-gray-600 mt-1">
            Анализ трендов в ваших каналах
          </p>
        </div>

        <div className="card text-center py-12">
          <div className="text-6xl mb-4">📱</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Telegram не подключён
          </h3>
          <p className="text-gray-600 mb-4">
            Подключите Telegram аккаунт для анализа трендов в каналах
          </p>
          <a href="/settings" className="btn-primary inline-block">
            Подключить Telegram
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="pt-14">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Тренды</h1>
          <p className="text-gray-600 mt-1">
            Анализ трендов и популярных тем в каналах
          </p>
        </div>
        <button className="btn-primary">
          🔄 Запустить анализ
        </button>
      </div>

      {/* Channel Filter */}
      <div className="card mb-6">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Канал:</label>
          <select
            value={selectedChannel || ''}
            onChange={(e) => setSelectedChannel(e.target.value ? Number(e.target.value) : null)}
            className="input max-w-xs"
          >
            <option value="">Все каналы</option>
            {channels.map((channel: any) => (
              <option key={channel.id} value={channel.id}>
                {channel.title}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Trends List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : trends.length === 0 ? (
        <div className="card text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Нет данных о трендах
          </h3>
          <p className="text-gray-600">
            Добавьте каналы и запустите анализ трендов
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {trends.map((trend) => (
            <TrendCard key={trend.id} trend={trend} />
          ))}
        </div>
      )}
    </div>
  )
}

function TrendCard({ trend }: { trend: TrendAnalysis }) {
  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
    })
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-900">{trend.channel_title}</h3>
          <p className="text-sm text-gray-500">
            {formatDate(trend.period_start)} — {formatDate(trend.period_end)}
          </p>
        </div>
        <span className="text-xs text-gray-400">
          {formatDate(trend.created_at)}
        </span>
      </div>

      {/* Top Topics */}
      {trend.top_topics.length > 0 && (
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700 mb-2">Топ темы:</p>
          <div className="flex flex-wrap gap-2">
            {trend.top_topics.map((topic, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
              >
                {topic}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Trending Keywords */}
      {trend.trending_keywords.length > 0 && (
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700 mb-2">Ключевые слова:</p>
          <div className="flex flex-wrap gap-2">
            {trend.trending_keywords.map((keyword, index) => (
              <span
                key={index}
                className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded"
              >
                #{keyword}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Insights */}
      {trend.engagement_insights && (
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-sm text-gray-700">{trend.engagement_insights}</p>
        </div>
      )}
    </div>
  )
}
